import React, { useEffect, useState, useMemo, useCallback } from "react";
import PrimaryButton from "../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../ui-library/Buttons/SecondaryButton/SecondaryButton";
import CustomDropdown from "../../ui-library/CustomDropdown/CustomDropdown";
import styled, { withTheme } from "styled-components";
import PropTypes from "prop-types";
import { studiesIds, studyStatusesGSDIII } from "../../../modules/constants";
import SetDailyGoals from "../SetDailyGoals/SetDailyGoals";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 36px;
  align-items: center;
`;

const FieldTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-weight: 600;
  font-size: 16px;
  margin-right: 14px;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 38px;
  @media (max-width: 576px) {
    padding-top: 2rem;
    margin-top: auto;
    .btn {
      width: 100%;
    }
  }
`;

const SetDailyGoalsContainer = styled.div`
  display: flex;
  padding: 13px 27px;
  background-color: ${(props) => props.theme?.whiteSmoke};
  margin-top: 27px;
`;

const StyledLabelledInput = styled(LabelledInput)`
  margin-top: 27px;
`;

const Legend = styled.span`
  color: black;
  font-weight: 600;
  font-size: 16px;
  margin-top: 16px;
`;

const ChangeStudyStatus = (props) => {
  const {
    currentStatus,
    onSubmit,
    onCancel,
    processing,
    values,
    hasError,
    studyId,
    currentDailyTargets,
  } = props;

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dailyGoals, setDailyGoals] = useState(null);
  const [validateDailyGoals, setValidateDailyGoals] = useState(false);
  const [updateReason, setUpdateReason] = useState("");

  const [updateReasonIsValid, setUpdateReasonIsValid] = useState(false);
  const [formValid, setFormValid] = useState(false);

  const [submittingWithDailyGoals, setSubmittingWithDailyGoals] =
    useState(false);
  const [reasonForChangeError, setReasonForChangeError] = useState(null);

  const studyStatusesIncludesNutritionalTargets = useMemo(() => {
    return [
      studyStatusesGSDIII.RD_ISV,
      studyStatusesGSDIII.RD_NOV,
      studyStatusesGSDIII.RD_STV,
      studyStatusesGSDIII.RD_TRATMENT,
    ];
  }, []);

  useEffect(() => {
    setDailyGoals(null);
    setFormValid(false);
  }, [selectedStatus]);

  useEffect(() => {
    if (dailyGoals) {
      setValidateDailyGoals(false);
    }
  }, [dailyGoals]);

  useEffect(() => {
    if (hasError) {
      setSubmittingWithDailyGoals(false);
    }
  }, [hasError]);

  useEffect(() => {
    setUpdateReasonIsValid(
      updateReason.replace(/\.|,/g, "").trim() !== "" &&
        updateReason.length <= 450
    );
  }, [updateReason]);

  const validateReason = (reason) => {
    setReasonForChangeError(null);
    if (reason.replace(/\.|,/g, "").trim() === "") {
      setReasonForChangeError("Please provide a reason");
    }
    if (reason.length > 450) {
      setReasonForChangeError(
        "Please enter a reason with less than 450 characters"
      );
    }
  };

  const onChangeDailyGoals = (valid, goals) => {
    setFormValid(valid);

    if (valid) {
      setDailyGoals(goals);
    } else {
      setValidateDailyGoals(false);
      setDailyGoals(null);
    }
  };

  const onSave = () => {
    if (selectedStatus) {
      if (studyStatusesIncludesNutritionalTargets.includes(selectedStatus)) {
        setValidateDailyGoals(true);
      } else {
        onSubmit(selectedStatus, null, updateReason);
      }
    }
  };

  const onChangeSelectedStatus = (value) => {
    const key = Object.keys(values).find(
      (statusKey) => values[statusKey].id === value.id
    );
    setSelectedStatus(values[key]);
  };

  const submitHandler = useCallback(() => {
    setSubmittingWithDailyGoals(true);
    onSubmit(selectedStatus, dailyGoals, updateReason);
  }, [dailyGoals, onSubmit, selectedStatus, updateReason]);

  useEffect(() => {
    if (dailyGoals && formValid && !submittingWithDailyGoals) {
      submitHandler();
    }
  }, [dailyGoals, formValid, submittingWithDailyGoals, submitHandler]);

  return (
    <Container data-testid="change-study-status">
      {currentStatus && (
        <Row>
          <FieldTitle>Current Status:</FieldTitle>
          <CustomDropdown
            placeholder={currentStatus.value}
            disabled
            width={190}
          />
        </Row>
      )}

      <Row>
        <FieldTitle>Update to:</FieldTitle>
        <CustomDropdown
          placeholder="Select"
          values={Object.values(values)}
          width={190}
          onChange={onChangeSelectedStatus}
        />
      </Row>

      {selectedStatus &&
        studyId.toLocaleLowerCase() !==
          studiesIds.lcfaod401.toLocaleLowerCase() && (
          <Legend>{`*${selectedStatus.value} - ${
            studyStatusesIncludesNutritionalTargets.includes(selectedStatus)
              ? "Include"
              : "No"
          } nutritional targets`}</Legend>
        )}

      {selectedStatus &&
        studyStatusesIncludesNutritionalTargets.includes(selectedStatus) && (
          <SetDailyGoalsContainer data-testid="setdailygoals-container">
            <SetDailyGoals
              onValidate={onChangeDailyGoals}
              validate={validateDailyGoals}
              defaultTargets={currentDailyTargets}
            />
          </SetDailyGoalsContainer>
        )}

      <StyledLabelledInput
        title="Reason for a status change:"
        type="text"
        data-testid="update-reason-input"
        value={updateReason}
        onChange={(e) => setUpdateReason(e.target.value)}
        onBlur={(e) => validateReason(e.target.value)}
        hasError={!!reasonForChangeError}
        errorMessage={reasonForChangeError}
      />

      {onSubmit && (
        <ActionButtonsContainer>
          {onCancel && (
            <SecondaryButton
              data-testid="cancel-button"
              className="mr-2"
              onClick={onCancel}
            >
              Cancel
            </SecondaryButton>
          )}
          <PrimaryButton
            data-testid="submit-button"
            disabled={!selectedStatus || !updateReasonIsValid || processing}
            onClick={onSave}
          >
            Save
          </PrimaryButton>
        </ActionButtonsContainer>
      )}
    </Container>
  );
};

ChangeStudyStatus.defaultProps = {
  onSubmit: null,
  currentStatus: null,
  hasError: false,
};

ChangeStudyStatus.propTypes = {
  currentStatus: PropTypes.object,
  onSubmit: PropTypes.func,
  hasError: PropTypes.bool,
  values: PropTypes.array.isRequired,
};

export default withTheme(ChangeStudyStatus);
